import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Armchair, MousePointer2, Trash2, RotateCw, AlertCircle, Eye, Lock, Info, X, Loader2,
} from 'lucide-react';
import StructureCanvas from './StructureCanvas';
import { usePlanEditor, FURNITURE_KINDS, furnitureKindFor } from './PlanEditorContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pointInPoly = (x, y, poly) => {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const [xi, yi] = Array.isArray(poly[i]) ? poly[i] : [poly[i].x, poly[i].y];
    const [xj, yj] = Array.isArray(poly[j]) ? poly[j] : [poly[j].x, poly[j].y];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-9) + xi) inside = !inside;
  }
  return inside;
};

const GROUPS = [...new Set(FURNITURE_KINDS.map((k) => k.group))];

/**
 * TabFurniture — V15.32 (Onglet 4 · Mobilier).
 * Pose des meubles à leurs dimensions réelles ; ils deviennent des gabarits verrouillés
 * sur le plan de repérage de l'étape 3 « Meubler » (l'IA les modélise à cet emplacement exact).
 * Les pièces sans meuble posé restent libres (Matériauthèque).
 */
const TabFurniture = ({ projectId, selectedLot, selectedLotId, savedState }) => {
  const isMultiLot = !!selectedLot || !!selectedLotId;
  const { setActiveSubTab, setMode, rooms, calibration } = usePlanEditor();
  const blockingReason = useMemo(() => {
    if (isMultiLot && !selectedLotId) return 'Sélectionnez un lot dans la barre latérale.';
    if (!savedState?.source_file_id) return 'Uploadez un plan dans l’étape 1 · Plan source.';
    if (!calibration?.scale_m_per_norm_unit) return 'Mettez le plan à l’échelle (1b) : les meubles sont posés à leurs dimensions réelles.';
    if (!(rooms || []).length) return 'Nommez les pièces (3 · Pièces) avant de les meubler.';
    return null;
  }, [isMultiLot, selectedLotId, savedState, calibration, rooms]);

  const bgImageUrl = useMemo(() => {
    if (!savedState?.source_file_id) return null;
    const fid = savedState?.cleaned_file_id || savedState.source_file_id;
    const endpoint = savedState?.cleaned_file_id ? 'render' : 'source';
    const lotQ = selectedLotId ? `&lot_id=${selectedLotId}` : '';
    return `${API}/projects/${projectId}/plan-source/${endpoint}?file_id=${fid}${lotQ}`;
  }, [projectId, savedState, selectedLotId]);

  useEffect(() => {
    setActiveSubTab('furniture');
    setMode('select');
    return () => setMode('select');
  }, [setActiveSubTab, setMode]);

  return (
    <div className="grid grid-cols-12 gap-3 h-full min-h-0" data-testid="tab-furniture-root">
      <div className="col-span-8 min-h-0 flex flex-col rounded-lg border overflow-hidden"
           style={{ background: 'rgba(0,0,0,0.25)', borderColor: 'var(--c-border)' }}>
        <FurnitureToolbar disabled={!!blockingReason} projectId={projectId} lotId={selectedLotId} />
        <div className="flex-1 min-h-0 relative">
          {blockingReason ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-6 text-center text-[12px] text-gray-400" data-testid="furniture-blocker">
              <AlertCircle className="w-6 h-6 text-amber-400" />
              <div className="max-w-[36ch]">{blockingReason}</div>
            </div>
          ) : (
            <StructureCanvas bgImageUrl={bgImageUrl} />
          )}
        </div>
        <div className="px-2.5 py-1 border-t flex items-center gap-2 text-[9.5px] text-gray-500" style={{ borderColor: 'var(--c-border)' }}>
          <Lock className="w-3 h-3 text-emerald-400" />
          Chaque meuble posé est verrouillé à l’étape 3 « Meubler » (même place, orientation, dimensions). Les pièces sans meuble restent libres.
        </div>
      </div>
      <div className="col-span-4 min-h-0 flex flex-col rounded-lg border overflow-hidden"
           style={{ background: 'rgba(255,255,255,0.02)', borderColor: 'var(--c-border)' }}>
        <FurniturePanel disabled={!!blockingReason} />
      </div>
      <FurnitureKeyboardShortcuts disabled={!!blockingReason} />
    </div>
  );
};

const FurnitureToolbar = ({ disabled, projectId, lotId }) => {
  const { mode, setMode, furniture, actions, saving, isDirty } = usePlanEditor();
  const placing = mode.startsWith('place:furniture:');
  const current = placing ? mode.slice('place:furniture:'.length) : '';
  const [preview, setPreview] = useState(null);
  return (
    <div className="flex items-center gap-1.5 px-2 py-1.5 border-b flex-wrap" style={{ borderColor: 'var(--c-border)' }} data-testid="furniture-toolbar">
      <button type="button" onClick={() => setMode('select')} disabled={disabled}
              className={`inline-flex items-center gap-1 h-7 px-2 rounded text-[11px] border ${!placing ? 'bg-white/10 border-white/20 text-white' : 'border-white/10 text-gray-300 hover:bg-white/5'}`}
              title="Sélectionner / déplacer (S)" data-testid="furniture-tool-select">
        <MousePointer2 className="w-3.5 h-3.5" /> Sélection
      </button>
      <div className="flex items-center gap-1" title="Choisissez un meuble puis cliquez dans une pièce pour le poser">
        <Armchair className={`w-3.5 h-3.5 ${placing ? 'text-emerald-300' : 'text-gray-400'}`} />
        <select value={current} disabled={disabled}
                onChange={(e) => setMode(e.target.value ? `place:furniture:${e.target.value}` : 'select')}
                className={`h-7 text-[11px] rounded px-1.5 border focus:outline-none ${placing ? 'bg-emerald-500/15 border-emerald-400/50 text-emerald-100' : 'bg-white/[0.04] border-white/10 text-gray-300'}`}
                data-testid="furniture-kind-select">
          <option value="">Poser un meuble…</option>
          {GROUPS.map((g) => (
            <optgroup key={g} label={g}>
              {FURNITURE_KINDS.filter((k) => k.group === g).map((k) => (
                <option key={k.value} value={k.value}>{k.label} · {k.dims[0].toFixed(2)} × {k.dims[1].toFixed(2)} m</option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
      <button type="button" disabled={disabled || !furniture?.length}
              onClick={() => setPreview(`${API}/projects/${projectId}/lots/${lotId}/whitebox-deterministic/preview?aspect=16:9&peripheral_cut=0.5&furniture_blocks=true&room_labels=true&t=${Date.now()}`)}
              className="inline-flex items-center gap-1 h-7 px-2 rounded text-[11px] border border-white/10 text-gray-300 hover:bg-white/5 disabled:opacity-40"
              title="Voir le plan de repérage avec les gabarits de mobilier tel que l’IA le recevra" data-testid="furniture-preview-btn">
        <Eye className="w-3.5 h-3.5" /> Aperçu gabarits
      </button>
      <div className="ml-auto flex items-center gap-2 text-[10px] text-gray-500">
        {saving ? <span className="inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Sauvegarde…</span>
          : isDirty ? 'Modifié' : furniture?.length ? `${furniture.length} meuble${furniture.length > 1 ? 's' : ''} verrouillé${furniture.length > 1 ? 's' : ''}` : 'Aucun meuble posé'}
        {!!furniture?.length && (
          <button type="button" disabled={disabled} onClick={() => { if (window.confirm('Retirer tous les meubles posés ?')) actions.clearAllFurniture(); }}
                  className="inline-flex items-center gap-1 text-rose-300/70 hover:text-rose-300" data-testid="furniture-clear-all">
            <Trash2 className="w-3 h-3" /> Tout retirer
          </button>
        )}
      </div>
      {preview && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-6" style={{ background: 'rgba(6,8,12,0.88)' }} onClick={() => setPreview(null)} data-testid="furniture-preview-modal">
          <div className="relative max-w-5xl w-full rounded-xl overflow-hidden" style={{ border: '1px solid var(--c-border)', background: '#fff' }} onClick={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => setPreview(null)} className="absolute top-2 right-2 rounded-full p-1.5 bg-black/70 text-white" data-testid="furniture-preview-close"><X className="w-4 h-4" /></button>
            <img src={preview} alt="Plan de repérage avec gabarits de mobilier" className="w-full h-auto block" data-testid="furniture-preview-img" />
            <div className="px-3 py-2 text-[11px] text-gray-700 bg-gray-50 flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Image envoyée à l’IA à l’étape 3 en plus du rendu texturé : les blocs colorés sont les gabarits à respecter.</div>
          </div>
        </div>
      )}
    </div>
  );
};

const FurniturePanel = ({ disabled }) => {
  const { furniture, selectedFurnitureId, setSelectedFurnitureId, actions, rooms, calibration } = usePlanEditor();
  const S = calibration?.scale_m_per_norm_unit || 0;
  const roomOf = useCallback((f) => {
    const cx = f.x + f.w / 2, cy = f.y + f.h / 2;
    const r = (rooms || []).find((rm) => (rm.polygon || []).length >= 3 && pointInPoly(cx, cy, rm.polygon));
    return r ? (r.label || r.type || 'Pièce') : 'Hors pièce';
  }, [rooms]);
  const grouped = useMemo(() => {
    const g = {};
    (furniture || []).forEach((f) => { const r = roomOf(f); (g[r] = g[r] || []).push(f); });
    return g;
  }, [furniture, roomOf]);

  if (!furniture?.length) {
    return (
      <div className="p-4 text-[11.5px] text-gray-400 space-y-2" data-testid="furniture-empty">
        <div className="flex items-center gap-1.5 text-gray-200 font-medium"><Armchair className="w-4 h-4 text-emerald-300" /> Mobilier verrouillé</div>
        <p>Choisissez un meuble dans la barre d’outils puis cliquez dans une pièce : il est posé à ses <strong>dimensions réelles</strong> (échelle du plan) et peut être déplacé, pivoté et redimensionné.</p>
        <p>À l’étape 3 « Meubler », l’IA reçoit ces gabarits et doit les modéliser <strong>exactement à cet emplacement</strong>, sans ajouter d’autre meuble majeur dans la pièce. Les pièces sans meuble posé restent libres.</p>
      </div>
    );
  }
  return (
    <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-2" data-testid="furniture-panel">
      {Object.entries(grouped).map(([room, items]) => (
        <div key={room}>
          <div className="text-[9.5px] uppercase tracking-[0.14em] text-gray-400 font-semibold px-1 mb-1 flex items-center gap-1.5" data-testid={`furniture-room-group-${room}`}>
            <Lock className="w-3 h-3 text-emerald-400" /> {room} <span className="text-gray-600">· {items.length}</span>
          </div>
          <div className="space-y-1">
            {items.map((f) => {
              const k = furnitureKindFor(f.kind);
              const isSel = f.id === selectedFurnitureId;
              const L = S > 0 ? f.w * S : null, P = S > 0 ? f.h * S : null;
              return (
                <div key={f.id}
                     className={`rounded border px-2 py-1.5 text-[11px] cursor-pointer transition-colors ${isSel ? 'border-white/40 bg-white/10' : 'border-white/10 hover:bg-white/[0.04]'}`}
                     onClick={() => setSelectedFurnitureId(isSel ? null : f.id)} data-testid={`furniture-row-${f.id}`}>
                  <div className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: k.color }} />
                    <span className="font-medium text-gray-100">{k.label}</span>
                    {L != null && <span className="text-gray-400 hud-tabular">{L.toFixed(2)} × {P.toFixed(2)} m</span>}
                    <button type="button" className="ml-auto text-rose-300/70 hover:text-rose-300" disabled={disabled}
                            onClick={(e) => { e.stopPropagation(); actions.deleteFurniture(f.id); }} title="Retirer" data-testid={`furniture-delete-${f.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                  {isSel && (
                    <div className="mt-1.5 space-y-1.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1.5">
                        <select value={f.kind} disabled={disabled}
                                onChange={(e) => { const nk = furnitureKindFor(e.target.value); actions.updateFurniture(f.id, { kind: nk.value, w: S > 0 ? nk.dims[0] / S : f.w, h: S > 0 ? nk.dims[1] / S : f.h }); }}
                                className="h-6 text-[10.5px] rounded px-1 bg-white/[0.06] border border-white/10 text-white flex-1" data-testid={`furniture-kind-${f.id}`}>
                          {FURNITURE_KINDS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                      </div>
                      {S > 0 && (
                        <div className="flex items-center gap-1.5 text-[10.5px] text-gray-300">
                          <label>L</label>
                          <input type="number" min={0.2} max={6} step={0.05} value={+L.toFixed(2)} disabled={disabled}
                                 onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v) && v > 0.1) actions.updateFurniture(f.id, { w: Math.min(1 - f.x, v / S) }); }}
                                 className="w-14 h-6 text-[11px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white text-right" data-testid={`furniture-length-${f.id}`} />
                          <label>P</label>
                          <input type="number" min={0.2} max={6} step={0.05} value={+P.toFixed(2)} disabled={disabled}
                                 onChange={(e) => { const v = parseFloat(e.target.value); if (Number.isFinite(v) && v > 0.1) actions.updateFurniture(f.id, { h: Math.min(1 - f.y, v / S) }); }}
                                 className="w-14 h-6 text-[11px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white text-right" data-testid={`furniture-depth-${f.id}`} />
                          <span className="text-gray-500">m</span>
                          <span className="ml-auto text-gray-500">H {k.dims[2].toFixed(2)} m</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1.5" data-testid={`furniture-rotation-row-${f.id}`}>
                        <RotateCw className="w-3 h-3 text-gray-400" />
                        <button type="button" disabled={disabled}
                                onClick={() => actions.updateFurniture(f.id, { rotation_deg: ((+f.rotation_deg || 0) + 90) % 360 })}
                                className="h-6 px-1.5 rounded text-[10px] border border-white/10 text-gray-300 hover:bg-white/5" data-testid={`furniture-rotate90-${f.id}`}>+90°</button>
                        <input type="range" min={0} max={359} step={1} value={Math.round(+f.rotation_deg || 0)} disabled={disabled}
                               onChange={(e) => actions.updateFurniture(f.id, { rotation_deg: parseInt(e.target.value, 10) })}
                               className="flex-1 accent-emerald-400" data-testid={`furniture-rotation-slider-${f.id}`} />
                        <span className="text-[10px] text-gray-400 w-8 text-right hud-tabular">{Math.round(+f.rotation_deg || 0)}°</span>
                      </div>
                      <input type="text" value={f.note || ''} placeholder="Précision pour l’IA (ex. velours vert, dossier côté mur)" disabled={disabled} maxLength={120}
                             onChange={(e) => actions.updateFurniture(f.id, { note: e.target.value })}
                             className="w-full h-6 text-[10.5px] px-1.5 rounded bg-white/[0.06] border border-white/10 text-white" data-testid={`furniture-note-${f.id}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const FurnitureKeyboardShortcuts = ({ disabled }) => {
  const { setMode, selectedFurnitureId, actions, furniture } = usePlanEditor();
  useEffect(() => {
    if (disabled) return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target?.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (k === 's') { setMode('select'); e.preventDefault(); }
      else if (k === 'escape') { setMode('select'); }
      else if ((k === 'delete' || k === 'backspace') && selectedFurnitureId) { actions.deleteFurniture(selectedFurnitureId); e.preventDefault(); }
      else if (k === 'r' && selectedFurnitureId) {
        const cur = (furniture || []).find((f) => f.id === selectedFurnitureId);
        if (cur) actions.updateFurniture(selectedFurnitureId, { rotation_deg: ((+cur.rotation_deg || 0) + 90) % 360 });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [disabled, setMode, selectedFurnitureId, actions, furniture]);
  return null;
};

export default TabFurniture;
